import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const PRIVACY_ET = `
**PRIVAATSUSPOLIITIKA**
*Viimati uuendatud: märts 2025*

Käesolev privaatsuspoliitika kirjeldab, kuidas **Polyus Plus OÜ** (registrikood 14890902, edaspidi „meie") töötleb isikuandmeid teenuse **Work+** kasutamisel. Dokument on koostatud kooskõlas Euroopa Parlamendi ja nõukogu määrusega (EL) 2016/679 (GDPR).

---

**1. VASTUTAV TÖÖTLEJA**

Polyus Plus OÜ  
Registrikood: 14890902  
E-post: info@polyusplus.com

---

**2. MILLISEID ANDMEID ME KOGUME**

**2.1 Kliendifirma registreerimisel:**
- Ettevõtte nimi, registrikood, aadress
- Administraatori nimi ja e-posti aadress
- Kontakttelefon

**2.2 Töötajate andmed (kliendifirma sisestab):**
- Töötaja täisnimi ja e-posti aadress
- Parool (krüpteeritult)
- Roll (töötaja/administraator/juht)

**2.3 GPS-asukoha andmed:**
- Geograafilised koordinaadid (laiuskraad, pikkuskraad)
- Kellaaeg ja kuupäev
- Objekt/tsoon, kus töötaja viibis

**2.4 Tehniline teave:**
- Seadme ja brauseri tüüp (sessiooni haldamiseks)
- IP-aadress (turvalisuse tagamiseks)
- Sisselogimiste ajalugu

---

**3. MIKS ME ANDMEID TÖÖTLEME (ÕIGUSLIK ALUS)**

| Eesmärk | Õiguslik alus (GDPR art.) |
|---------|--------------------------|
| Tööaja arvestus | Art. 6(1)(b) – leping töötajaga; Art. 6(1)(c) – seaduslik kohustus (tööajaregistri pidamine) |
| GPS-jälgimine tööajal | Art. 6(1)(b) – töölepingust tulenev kohustus; Art. 6(1)(a) – nõusolek (vajalik töötajalt) |
| Konto haldamine | Art. 6(1)(b) – lepingu täitmine |
| Turvalisus ja petuste tõkestamine | Art. 6(1)(f) – õigustatud huvi |

---

**4. GPS-ANDMETE TÖÖTLEMINE**

**4.1 Kogumise põhimõte:** GPS-koordinaate kogutakse ainult siis, kui töötaja on ise aktiveerinud jälgimise rakenduses. Süsteem ei jälgi töötajaid pidevalt ega puhkepäevadel.

**4.2 Täpsus:** GPS-asukohamääramine on ligikaudne (täpsus 3–50 meetrit). Tööaja arvestus põhineb geograafilisel tsoonis viibimise faktil, mitte täpsel asukohal.

**4.3 Töötajate teavitamine:** Kliendifirma on kohustatud töötajaid GPS-jälgimisest teavitama enne kasutuselevõttu ja dokumenteerima nende nõusoleku (GDPR art. 13).

---

**5. ANDMETE SÄILITAMINE**

- **Tööajaandmed (tunnid, sisselogimised):** 7 aastat (raamatupidamise seaduse nõue)
- **GPS-koordinaatide ajalugu:** 3 aastat (tööõiguslikud nõuded)
- **Konto andmed:** kuni konto kustutamiseni + 30 päeva
- **Turvalogid:** 12 kuud

---

**6. ANDMETE JAGAMINE KOLMANDATE OSAPOOLTEGA**

Me ei müü ega jaga teie isikuandmeid kolmandate osapooltega, välja arvatud:

- **Pilveinfrastruktuur:** Serverid asuvad Euroopa Liidus (EU hostingupakkuja)
- **Seadusliku kohustuse täitmiseks:** Ametiasutuste põhjendatud nõuete korral

---

**7. TEIE ÕIGUSED (GDPR)**

Teil on õigus:

- **Tutvuda** oma andmetega (art. 15)
- **Parandada** ebaõigeid andmeid (art. 16)
- **Kustutada** andmeid ("unustuseõigus", art. 17) — v.a seaduslikul kohustusel säilitatavad andmed
- **Piirata** töötlemist (art. 18)
- **Andmete ülekandmisele** (art. 20)
- **Esitada vastuväide** töötlemisele (art. 21)
- **Kaevata** Andmekaitse Inspektsioonile (www.aki.ee)

Õiguste teostamiseks pöörduge: info@polyusplus.com

---

**8. ANDMEKAITSE JA TURVALISUS**

Rakendame tehnilisi ja organisatsioonilisi meetmeid andmete kaitsmiseks:
- Paroolid salvestatakse bcrypt-algoritmiga räsitult
- HTTPS-ühendus kõigi andmeedastuste jaoks
- Juurdepääs andmetele ainult volitatud kasutajatele
- Regulaarsed turvaülevaatused

---

**9. KÜPSISED**

Kasutame ainult tehniliselt vajalikke küpsiseid sessiooni haldamiseks. Reklaamiküpsiseid ei kasutata.

---

**10. MUUDATUSED**

Privaatsuspoliitika muutumisest teavitame e-posti teel vähemalt 30 päeva ette. Jätkates teenuse kasutamist, nõustute uuendatud tingimustega.

---

**11. KONTAKT**

Küsimused, nõusoleku tühistamine, andmetele juurdepääsu taotlused:  
**info@polyusplus.com**
`;

const PRIVACY_EN = `
**PRIVACY POLICY**
*Last updated: March 2025*

This Privacy Policy describes how **Polyus Plus OÜ** (registry code 14890902, hereinafter "we") processes personal data when using the **Work+** service. This document is prepared in accordance with Regulation (EU) 2016/679 of the European Parliament and of the Council (GDPR).

---

**1. DATA CONTROLLER**

Polyus Plus OÜ  
Registry code: 14890902  
Email: info@polyusplus.com

---

**2. WHAT DATA WE COLLECT**

**2.1 Upon client company registration:**
- Company name, registry code, address
- Administrator name and email address
- Contact phone

**2.2 Employee data (entered by the client company):**
- Employee full name and email address
- Password (encrypted)
- Role (employee/administrator/manager)

**2.3 GPS location data:**
- Geographic coordinates (latitude, longitude)
- Time and date
- Work site/zone where the employee was present

**2.4 Technical information:**
- Device and browser type (for session management)
- IP address (for security purposes)
- Login history

---

**3. WHY WE PROCESS DATA (LEGAL BASIS)**

| Purpose | Legal basis (GDPR art.) |
|---------|------------------------|
| Working hours tracking | Art. 6(1)(b) – contract with employee; Art. 6(1)(c) – legal obligation (working time records) |
| GPS tracking during working hours | Art. 6(1)(b) – obligation from employment contract; Art. 6(1)(a) – consent (required from employee) |
| Account management | Art. 6(1)(b) – performance of contract |
| Security and fraud prevention | Art. 6(1)(f) – legitimate interest |

---

**4. GPS DATA PROCESSING**

**4.1 Collection principle:** GPS coordinates are collected only when the employee has activated tracking themselves in the application. The system does not continuously track employees on days off.

**4.2 Accuracy:** GPS location determination is approximate (accuracy 3–50 meters). Working time accounting is based on the fact of being in a geographic zone, not on exact location.

**4.3 Employee notification:** The client company is obligated to notify employees of GPS tracking before implementation and to document their consent (GDPR art. 13).

---

**5. DATA RETENTION**

- **Working time data (hours, logins):** 7 years (accounting law requirement)
- **GPS coordinate history:** 3 years (employment law requirements)
- **Account data:** Until account deletion + 30 days
- **Security logs:** 12 months

---

**6. SHARING DATA WITH THIRD PARTIES**

We do not sell or share your personal data with third parties, except:

- **Cloud infrastructure:** Servers are located in the European Union (EU hosting provider)
- **To fulfill legal obligations:** In response to justified requests from authorities

---

**7. YOUR RIGHTS (GDPR)**

You have the right to:

- **Access** your data (art. 15)
- **Rectify** inaccurate data (art. 16)
- **Erase** data ("right to be forgotten", art. 17) — except data retained by legal obligation
- **Restrict** processing (art. 18)
- **Data portability** (art. 20)
- **Object** to processing (art. 21)
- **Lodge a complaint** with the Data Protection Inspectorate (www.aki.ee)

To exercise your rights, contact: info@polyusplus.com

---

**8. DATA SECURITY**

We implement technical and organizational measures to protect data:
- Passwords are stored as bcrypt hashes
- HTTPS connection for all data transfers
- Access to data only by authorized users
- Regular security reviews

---

**9. COOKIES**

We use only technically necessary cookies for session management. No advertising cookies are used.

---

**10. CHANGES**

We will notify you of changes to the Privacy Policy by email at least 30 days in advance. By continuing to use the service, you agree to the updated terms.

---

**11. CONTACT**

Questions, consent withdrawal, data access requests:  
**info@polyusplus.com**
`;

export default function Privacy() {
  const { language } = useLanguage();
  const [lang, setLang] = useState(language === 'et' ? 'et' : 'en');

  const content = lang === 'et' ? PRIVACY_ET : PRIVACY_EN;

  const renderMarkdown = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**')) {
        return <h2 key={i} className="text-xl font-bold text-white mt-8 mb-3">{line.slice(2, -2)}</h2>;
      }
      if (line.startsWith('---')) {
        return <hr key={i} className="border-wp-dark/50 my-4" />;
      }
      if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) {
        return <p key={i} className="text-wp-light/40 text-sm italic mb-4">{line.slice(1, -1)}</p>;
      }
      if (line.startsWith('| ') && line.includes('|')) {
        const cells = line.split('|').filter(c => c.trim());
        if (line.includes('---')) return null;
        return (
          <div key={i} className="flex gap-2 text-sm border-b border-wp-dark/30 py-2">
            {cells.map((cell, j) => (
              <div key={j} className={`flex-1 ${j === 0 ? 'text-white font-medium' : 'text-wp-light/70'}`}>{cell.trim()}</div>
            ))}
          </div>
        );
      }
      if (line.startsWith('**') && line.includes('**')) {
        const parts = line.split(/\*\*/g);
        return (
          <p key={i} className="text-wp-light/80 mb-2 leading-relaxed">
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-white">{part}</strong> : part)}
          </p>
        );
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="text-wp-light/70 ml-6 mb-1 list-disc leading-relaxed">{line.slice(2)}</li>;
      }
      if (line === '') {
        return <div key={i} className="h-2" />;
      }
      return <p key={i} className="text-wp-light/70 mb-2 leading-relaxed">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] p-4">
      <div className="absolute top-4 right-4 z-20">
        <LanguageSwitcher variant="auth" />
      </div>

      <div className="max-w-3xl mx-auto pt-16 pb-20">
        <div className="flex items-center justify-between mb-8">
          <Link to="/register-company" className="text-wp-light/50 hover:text-wp-light text-sm transition-colors">
            ← {lang === 'et' ? 'Tagasi registreerimisele' : 'Back to registration'}
          </Link>
          <div className="flex gap-2">
            <button
              onClick={() => setLang('et')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${lang === 'et' ? 'bg-indigo-600 text-white' : 'text-wp-light/50 hover:text-wp-light'}`}
            >
              ET
            </button>
            <button
              onClick={() => setLang('en')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${lang === 'en' ? 'bg-indigo-600 text-white' : 'text-wp-light/50 hover:text-wp-light'}`}
            >
              EN
            </button>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-8">
          <div className="prose prose-invert max-w-none">
            {renderMarkdown(content)}
          </div>
        </div>
      </div>
    </div>
  );
}
